import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { MrbBaseViewStore } from "mrb/core/stores";
import { applicationDefaults, applicationErrorCodes, subscriptionPlans, subscriptionStatuses } from "common/constants";
import { dateFormatter } from "mrb/common/formatting";
import { NakiRadioFreePlan } from "application/payment/models";
import ReactGA from "react-ga4";

// Held off for v1: applying a coupon to an already-active subscription mid-cycle has billing nuances (Stripe's
// "N months free" runs from the moment applied and can't refund the current already-paid cycle, so the user gets
// fewer free cycles than expected). The full implementation is intentionally left in place — the MyPlansCouponField
// UI, this store's coupon state/logic, PaymentService.applyPromoCode, PremiumSubscriptionStore.applyPromoCodeToSubscription,
// and the backend POST payment/apply-promo-code endpoint. Flip this flag to true to re-enable.
const MY_PLANS_COUPON_FIELD_ENABLED = false;

class UserSubscriptionsPreviewViewStore extends MrbBaseViewStore {
    @observable couponCode = "";
    @observable couponErrorCode = null;
    @observable isApplyingCoupon = false;

    @computed get activeSubscription() {
        return this.preprocessSubscription(this.rootStore.premiumSubscriptionStore.subscription);
    }

    @computed get showCouponField() {
        if (!MY_PLANS_COUPON_FIELD_ENABLED) {
            // Feature held off for v1 — see note at the top of this file. Re-enable via MY_PLANS_COUPON_FIELD_ENABLED.
            return false;
        }
        const subscription = this.activeSubscription;
        if (!subscription || subscription.id === "-1") {
            // No real subscription (free / fallback).
            return false;
        }
        if (subscription.product && subscription.product.slug === subscriptionPlans.nakiRadioFree) {
            return false;
        }
        if (this.pendingSubscription) {
            // Mid plan-change — don't offer a coupon.
            return false;
        }
        if (subscription.discount) {
            // A discount is already active — no stacking.
            return false;
        }
        return subscription.subscriptionStatus && subscription.subscriptionStatus.abrv === subscriptionStatuses.active;
    }

    @computed get couponErrorMessageKey() {
        switch (this.couponErrorCode) {
            case applicationErrorCodes.payment.promoCodeExpired:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.EXPIRED";
            case applicationErrorCodes.payment.promoCodeRedemptionLimitReached:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.LIMIT_REACHED";
            case applicationErrorCodes.payment.promoCodeRestrictionNotMet:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.RESTRICTION";
            case applicationErrorCodes.payment.promoCodeAlreadyRedeemed:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.ALREADY_USED";
            case applicationErrorCodes.payment.promoCodeNewCustomersOnly:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NEW_CUSTOMERS_ONLY";
            case applicationErrorCodes.payment.promoCodeNotFound:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.NOT_VALID";
            case null:
            case undefined:
                return null;
            default:
                return "APPLICATION.PREMIUM.PAYMENT.COUPON.ERRORS.GENERIC";
        }
    }

    @action.bound
    onChangeCouponCode(value) {
        this.couponCode = value;
        this.couponErrorCode = null;
    }

    @action.bound
    async applyCoupon() {
        const code = (this.couponCode || "").trim();
        if (!code) {
            return;
        }
        this.isApplyingCoupon = true;
        this.couponErrorCode = null;
        try {
            await this.rootStore.premiumSubscriptionStore.applyPromoCodeToSubscription(code);
            runInAction(() => {
                this.couponCode = "";
            });
            this.rootStore.notificationStore.success("Coupon applied. The discount will apply from your next billing cycle.");
        } catch (err) {
            runInAction(() => {
                this.couponErrorCode = err && err.data ? err.data.errorCode : null;
            });
        } finally {
            runInAction(() => {
                this.isApplyingCoupon = false;
            });
        }
    }

    @computed get pendingSubscription() {
        return this.rootStore.premiumSubscriptionStore.pendingSubscription;
    }

    @computed get isSubscriptionBundleActive() {
        return this.rootStore.premiumSubscriptionStore.isSubscriptionBundleActive;
    }

    constructor(rootStore, { routeStore }) {
        super(rootStore);
        makeObservable(this);
        this.routeStore = routeStore;

        const route = this.rootStore.routerStore.getCurrentRoute();
        if (route && route.data && route.data.title) {
            this.title = route.data.title;
        }
    }

    @action.bound
    setIsCanceling(isCanceling) {
        this.isCanceling = isCanceling;
    }

    @action.bound
    endTrial(subscriptionId) {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "End Premium trial?",
            onConfirm: async () => {
                try {
                    await this.rootStore.premiumSubscriptionStore.endTrialPeriod(subscriptionId);
                } catch (err) {
                    this.rootStore.notificationStore.error("Unable to end trial. Please contact the support team.");
                }
            },
            yesLabel: "End Trial",
            noLabel: "Cancel",
            description:
                "Your trial will end immediately, and you won't be able to access Premium features or activate trial anymore.",
        });
    }

    @action.bound
    cancelSubscription() {
        let description;
        if (this.activeSubscription.isInternal) {
            description =
                "Your subscription will end immediately, and you won't be able to access Premium features anymore.";
        } else {
            description = `You'll still be able to access Premium content until ${dateFormatter.format(
                this.activeSubscription.nextBillingDate,
                {
                    format: applicationDefaults.formats.date,
                }
            )}.`;
        }
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Cancel Subscription?",
            onConfirm: async () => {
                try {
                    const subscriptionPlan = this.activeSubscription.product.name;
                    await this.rootStore.premiumSubscriptionStore.cancelSubscription(this.activeSubscription.id);
                    ReactGA.event("unsubscribe", {
                        plan: subscriptionPlan,
                        internal_user_id: this.rootStore.userStore.user.id,
                    });
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "Unable to cancel subscription. Please contact the support team."
                    );
                }
            },
            yesLabel: "Cancel Subscription",
            noLabel: "Cancel",
            description: description,
        });
    }

    @action.bound
    renewSubscription() {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Renew Subscription?",
            onConfirm: async () => {
                try {
                    await this.rootStore.premiumSubscriptionStore.renewSubscription(this.activeSubscription.id);
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "Unable to renew subscription. Please contact the support team."
                    );
                }
            },
            yesLabel: `Renew ${this.activeSubscription.product.name}`,
            noLabel: "Cancel",
            description: `Your ${this.activeSubscription.product.name} subscription won't be canceled and you can continue enjoying Premium content.`,
        });
    }

    @action.bound
    revertSubscription() {
        this.rootStore.confirmDialogStore.showConfirm({
            message: "Revert Subscription?",
            onConfirm: async () => {
                try {
                    await this.rootStore.premiumSubscriptionStore.renewSubscription(this.activeSubscription.id);
                } catch (err) {
                    this.rootStore.notificationStore.error(
                        "Unable to revert subscription. Please contact the support team."
                    );
                }
            },
            yesLabel: `Revert to ${this.activeSubscription.product.name}`,
            noLabel: "Cancel",
            description: `Your ${this.activeSubscription.product.name} subscription won't be canceled and you can continue using Premium content.`,
        });
    }

    preprocessSubscription(subscription) {
        if (subscription) {
            return subscription;
        }

        return {
            id: "-1",
            amount: 0,
            product: new NakiRadioFreePlan(),
            subscriptionStatus: {
                name: "Active",
                abrv: subscriptionStatuses.active,
            },
        };
    }
}

export default UserSubscriptionsPreviewViewStore;
